import { Component, Input, OnInit, forwardRef, OnDestroy, ChangeDetectorRef } from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
  Validator,
  AbstractControl,
  ValidationErrors,
  NG_VALIDATORS
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { Subject, debounceTime, take, takeUntil } from 'rxjs';
import { RecruitmentUser as User, RecruitmentDirection as Direction, RecruitmentServiceFonctionnel as ServiceFonctionnel } from '@models/domains/recruitment';
import { DestinatairesFormValue } from '@models/api/requests';
import { AgentsService } from 'src/app/core/services/agents.service';

@Component({
    selector: 'app-destinataires-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule
],
    templateUrl: './destinataires-form.component.html',
    styleUrls: ['./destinataires-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DestinatairesFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DestinatairesFormComponent),
            multi: true
        }
    ]
})
export class DestinatairesFormComponent implements OnInit, ControlValueAccessor, OnDestroy, Validator {
  private _readOnly = false;
  @Input()
  set readOnly(value: boolean) {
    const nextValue = !!value;
    if (this._readOnly === nextValue) {
      return;
    }
    this._readOnly = nextValue;
    this.applyInteractivityForAllControls();
    this.handleDisplayOnlyToggle();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }
  @Input() demandeType = 'Recrutement';
  @Input()
  set direction(value: Direction | any | null) {
    const previousId = this._direction?.id ?? null;
    this._direction = value ?? null;
    const nextId = this._direction?.id ?? null;
    if (previousId !== nextId) {
      this.triggerStructureReload('direction');
    }
    if (this.isDisplayOnly()) {
      this.refreshGpeecLabelForDisplay();
    }
  }
  get direction(): Direction | any | null {
    return this._direction;
  }

  @Input()
  set service(value: ServiceFonctionnel | any | null) {
    const previousId = this._service?.id ?? null;
    this._service = value ?? null;
    const nextId = this._service?.id ?? null;
    if (previousId !== nextId) {
      this.triggerStructureReload('service');
    }
    if (this.isDisplayOnly()) {
      this.refreshGpeecLabelForDisplay();
    }
  }
  get service(): ServiceFonctionnel | any | null {
    return this._service;
  }

  form: FormGroup;

  // Lists for dropdowns
  directeursList: User[] = [];
  gpeecList: User[] = [];
  drhList: User[] = [];
  dgList: User[] = [];
  responsableList: User[] = [];
  dgsList: User[] = [];
  archivageRhList: User[] = [];

  // Field visibility flags
  showDestinataireDirecteurField = true;
  showDestinataireGpeecField = true;
  showDestinataireDrhField = true;
  showDestinataireDgField = true;
  showDestinataireResponsableField = true;
  showDestinataireDgsField = true;
  showDestinataireArchivageRhField = true;

  // Label for GPEEC field
  libelleGpeecListe = 'Chargé de gestion GPEEC';

  private readonly destroy$ = new Subject<void>();
  private readonly structureChange$ = new Subject<'direction' | 'service'>();
  private _direction: Direction | any | null = null;
  private _service: ServiceFonctionnel | any | null = null;
  private isDisabledByParent = false;
  private lastDisplayOnly = false;
  private latestStructureReloadId = 0;
  private latestGpeecLabelReloadId = 0;
  private onChange: (value: DestinatairesFormValue) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly agentsService: AgentsService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      destinataires: [[]],
      destinataireValidation: [null, Validators.required],
      destinataireChargeGpec: [null, Validators.required],
      destinataireDrh: [null, Validators.required],
      destinataireDg: [null, Validators.required],
      destinataireResponsable: [null, Validators.required],
      destinataireDgs: [null, Validators.required],
      destinataireArchivageRh: [null, Validators.required]
    });
  }

  // Sets up value/status watchers and deferred structure reloads.
  ngOnInit(): void {
    this.structureChange$
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe((reason) => this.reloadStructureDependentDestinataires(reason));

    // Subscribe to form value changes to notify parent
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onChange(this.form.getRawValue());
      this.onTouched();
    });

    // Subscribe to form status changes to notify parent of validation changes
    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onValidationChange();
    });

    this.lastDisplayOnly = this.isDisplayOnly();
  }

  // ControlValueAccessor methods
  // Populates the control from the parent value and loads supporting lists.
  writeValue(value: DestinatairesFormValue): void {
    if (value) {
      if (value.directionDemande) {
        this._direction = value.directionDemande;
      }
      this.form.patchValue(value, { emitEvent: false });
      if (this.isDisplayOnly()) {
        this.populateReadOnlyOptions(value);
        this.refreshParentValidation();
        return;
      }
      this.initializeEditableData(value, true);
    }
  }

  // Exposes the aggregated validity of the internal form group.
  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { invalidDestinatairesData: { invalidControls: this.getInvalidControls() } };
  }

  // Stores the parent callback to propagate value changes.
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Stores the parent callback to mark the control as touched.
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Stores the parent callback triggered when validations change.
  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }

  // Mirrors the parent disabled state onto all internal controls.
  setDisabledState(isDisabled: boolean): void {
    this.isDisabledByParent = isDisabled;
    this.applyInteractivityForAllControls();
    this.handleDisplayOnlyToggle();
  }

  // Load methods for different destinataire types
  // Fetch Directeur validators only when the form is interactive.
  private loadDestinataireDirecteur(
    currentValue: User | null,
    directionId: string | null,
    preferExistingSelection: boolean,
    reloadId: number
  ): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireValidation');
    if (!directionId) {
      this.hideDestinataireDirecteurField();
      return;
    }

    this.agentsService
      .getDestinataireDirecteur(this.demandeType, directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (reloadId !== this.latestStructureReloadId) {
            return;
          }
          const apiValideurs: User[] = data?.valideurs ?? [];
          const options = preferExistingSelection ? this.mergeCurrentSelection(apiValideurs, currentValue) : apiValideurs;
          if (!options.length) {
            this.hideDestinataireDirecteurField();
            return;
          }

          this.showDestinataireDirecteurField = true;
          this.directeursList = options;
          const selected = this.selectCandidate(apiValideurs, currentValue, preferExistingSelection);
          control?.setValidators(Validators.required);
          this.setControlInteractivity(control, true);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération Directeur :', error)
      });
  }

  // Keeps the GPEEC select synchronised with direction/service in editable mode.
  private loadDestinataireGpeec(
    currentValue: User | null,
    directionId: string | null,
    serviceId: string | null,
    preferExistingSelection: boolean,
    reloadId: number
  ): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireChargeGpec');
    if (!directionId) {
      this.hideDestinataireGpeecField();
      return;
    }

    this.agentsService
      .getChargedGPEEC(this.demandeType, directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (reloadId !== this.latestStructureReloadId) {
            return;
          }
          const apiValideurs: User[] = data?.valideurs ?? [];

          this.libelleGpeecListe = this.getGpeecLabel(data?.type);

          const options = preferExistingSelection ? this.mergeCurrentSelection(apiValideurs, currentValue) : apiValideurs;
          if (!options.length) {
            this.hideDestinataireGpeecField();
            return;
          }

          this.showDestinataireGpeecField = true;
          this.gpeecList = options;
          const selected = this.selectCandidate(apiValideurs, currentValue, preferExistingSelection);
          control?.setValidators(Validators.required);
          this.setControlInteractivity(control, true);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération GPEEC :', error)
      });
  }

  // Refresh the DRH list only when user input can change it.
  private loadDestinataireDrh(currentValue: User | null): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireDrh');

    this.agentsService
      .getDestinataireDRH(this.demandeType)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const valideurs: User[] = this.mergeCurrentSelection(data?.valideurs ?? [], currentValue);

          if (!valideurs.length) {
            this.hideDestinataireDrhField();
            return;
          }

          this.showDestinataireDrhField = true;
          this.drhList = valideurs;
          const selected = this.retainOrDefault(valideurs, currentValue);
          control?.setValidators(Validators.required);
          this.setControlInteractivity(control, true);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération DRH :', error)
      });
  }

  // Reload the DG validator list when the direction changes in edit mode.
  private loadDestinataireDg(
    currentValue: User | null,
    directionId: string | null,
    preferExistingSelection: boolean,
    reloadId: number
  ): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireDg');
    if (!directionId) {
      this.hideDestinataireDgField();
      return;
    }

    this.agentsService
      .getDestinataireDG(this.demandeType, directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (reloadId !== this.latestStructureReloadId) {
            return;
          }
          const apiValideurs: User[] = data?.valideurs ?? [];
          const options = preferExistingSelection ? this.mergeCurrentSelection(apiValideurs, currentValue) : apiValideurs;
          if (!options.length) {
            this.hideDestinataireDgField();
            return;
          }

          this.showDestinataireDgField = true;
          this.dgList = options;
          const selected = this.selectCandidate(apiValideurs, currentValue, preferExistingSelection);
          control?.setValidators(Validators.required);
          this.setControlInteractivity(control, true);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération DG :', error)
      });
  }

  // Responsable validators depend on both direction and service; skip in read-only.
  private loadDestinataireResponsable(
    currentValue: User | null,
    directionId: string | null,
    serviceId: string | null,
    preferExistingSelection: boolean,
    reloadId: number
  ): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireResponsable');
    if (!serviceId || !directionId) {
      this.hideDestinataireResponsableField();
      return;
    }

    this.agentsService
      .getDestinataireResponsable(this.demandeType, serviceId, directionId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (reloadId !== this.latestStructureReloadId) {
            return;
          }
          const apiValideurs: User[] = data?.valideurs ?? [];
          const options = preferExistingSelection ? this.mergeCurrentSelection(apiValideurs, currentValue) : apiValideurs;
          if (!options.length) {
            this.hideDestinataireResponsableField();
            return;
          }

          this.enableDestinataireResponsableField();
          this.responsableList = options;
          const selected = this.selectCandidate(apiValideurs, currentValue, preferExistingSelection);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération Responsable :', error)
      });
  }

  // Fetch the DGS validator list only for interactive workflows.
  private loadDestinataireDgs(currentValue: User | null): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireDgs');

    this.agentsService
      .getDestinataireDGS(this.demandeType)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          const valideurs: User[] = this.mergeCurrentSelection(data?.valideurs ?? [], currentValue);

          if (!valideurs.length) {
            this.hideDestinataireDgsField();
            return;
          }

          this.showDestinataireDgsField = true;
          this.dgsList = valideurs;
          const selected = this.retainOrDefault(valideurs, currentValue);
          control?.setValidators(Validators.required);
          this.setControlInteractivity(control, true);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération DGS :', error)
      });
  }

  // Reload Archivage RH when structural data changes while editing.
  private loadDestinataireArchivageRh(
    currentValue: User | null,
    directionId: string | null,
    preferExistingSelection: boolean,
    reloadId: number,
    serviceId: string | null
  ): void {
    if (this.isDisplayOnly()) {
      return;
    }
    const control = this.form.get('destinataireArchivageRh');
    if (!directionId) {
      this.hideDestinataireArchivageRhField();
      return;
    }

    this.agentsService
      .getDestinataireArchivageRh(this.demandeType, directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (reloadId !== this.latestStructureReloadId) {
            return;
          }
          const apiValideurs: User[] = data?.valideurs ?? [];
          const options = preferExistingSelection ? this.mergeCurrentSelection(apiValideurs, currentValue) : apiValideurs;
          if (!options.length) {
            this.hideDestinataireArchivageRhField();
            return;
          }

          this.showDestinataireArchivageRhField = true;
          this.archivageRhList = options;
          const selected = this.selectCandidate(apiValideurs, currentValue, preferExistingSelection);
          control?.setValidators(Validators.required);
          this.setControlInteractivity(control, true);
          control?.setValue(selected, { emitEvent: false });
          this.emitValueToParent();
          this.refreshParentValidation();
        },
        error: (error) => console.error('Erreur lors de la récupération Archivage RH :', error)
      });
  }

  // Kicks the debounce pipeline so structure-dependent lists update.
  private triggerStructureReload(reason: 'direction' | 'service'): void {
    this.structureChange$.next(reason);
  }

  // Refreshes every validator list that depends on direction/service when editable.
  private reloadStructureDependentDestinataires(reason: 'direction' | 'service' | 'init', preferExistingSelection = false): void {
    if (this.isDisplayOnly()) {
      return;
    }
    if (!this.form) {
      return;
    }

    const currentValues = (this.form.getRawValue() as Partial<DestinatairesFormValue>) ?? {};
    const directionId = this.extractId(this.direction ?? currentValues.directionDemande ?? null);
    const serviceId = this.extractId(this.service ?? null);
    const reloadId = ++this.latestStructureReloadId;

    if (!directionId) {
      this.hideDestinataireDirecteurField();
      this.hideDestinataireDgField();
      this.hideDestinataireArchivageRhField();
      this.hideDestinataireGpeecField();
      this.hideDestinataireResponsableField();
      return;
    }

    const shouldReloadDirectionDependents = reason === 'direction' || reason === 'init';
    const shouldReloadServiceDependents = reason === 'service' || reason === 'direction' || reason === 'init';
    const shouldReloadArchivage = shouldReloadDirectionDependents || reason === 'service';

    if (shouldReloadDirectionDependents) {
      this.loadDestinataireDirecteur(currentValues.destinataireValidation ?? null, directionId, preferExistingSelection, reloadId);
      this.loadDestinataireDg(currentValues.destinataireDg ?? null, directionId, preferExistingSelection, reloadId);
    }
    if (shouldReloadArchivage) {
      this.loadDestinataireArchivageRh(
        currentValues.destinataireArchivageRh ?? null,
        directionId,
        preferExistingSelection,
        reloadId,
        serviceId
      );
    }

    if (shouldReloadServiceDependents) {
      this.loadDestinataireResponsable(
        currentValues.destinataireResponsable ?? null,
        directionId,
        serviceId,
        preferExistingSelection,
        reloadId
      );
      this.loadDestinataireGpeec(currentValues.destinataireChargeGpec ?? null, directionId, serviceId, preferExistingSelection, reloadId);
    }
  }

  // Keeps the currently selected validator when still available, otherwise defaults to the first entry.
  private retainOrDefault(list: User[], currentValue: User | null): User | null {
    const currentId = this.extractId(currentValue);
    if (currentId) {
      const matching = list.find((user) => this.extractId(user) === currentId);
      if (matching) {
        return matching;
      }
    }
    return list[0] ?? null;
  }

  // Ensures a pre-selected value remains available even if the API no longer returns it.
  private mergeCurrentSelection(list: User[], currentValue: User | null): User[] {
    const currentId = this.extractId(currentValue);
    if (!currentId) {
      return list;
    }
    const existsInList = list.some((user) => this.extractId(user) === currentId);
    return existsInList ? list : [...list, currentValue as User];
  }

  private selectCandidate(list: User[], currentValue: User | null, preferExistingSelection: boolean): User | null {
    const currentId = this.extractId(currentValue);
    if (currentId) {
      const matching = list.find((user) => this.extractId(user) === currentId);
      if (matching) {
        return matching;
      }
    }
    if (preferExistingSelection) {
      return currentValue ?? list[0] ?? null;
    }
    return list[0] ?? null;
  }

  // Normalises the various entity shapes to a comparable string identifier.
  private extractId(entity: any): string | null {
    if (!entity) return null;
    if (typeof entity === 'string') return entity;
    if (typeof entity === 'number') return entity.toString();
    if (entity && typeof entity === 'object' && 'id' in entity) {
      return (entity as { id: string | number | null | undefined }).id?.toString() ?? null;
    }
    return null;
  }

  // Recomputes control validity and notifies the parent validator hook.
  private refreshParentValidation(): void {
    this.form.updateValueAndValidity({ emitEvent: false });
    this.onValidationChange();
  }

  // Prepares server-driven lists when the component operates in editable mode.
  private initializeEditableData(value: Partial<DestinatairesFormValue>, preferExistingSelection = false): void {
    this.loadDestinataireDrh(value.destinataireDrh ?? null);
    this.loadDestinataireDgs(value.destinataireDgs ?? null);
    this.reloadStructureDependentDestinataires('init', preferExistingSelection);
    this.refreshParentValidation();
  }

  // Reuses persisted values to populate selects without hitting the API in read-only mode.
  private populateReadOnlyOptions(value: Partial<DestinatairesFormValue>): void {
    this.directeursList = this.singleValueToList(value.destinataireValidation ?? null);
    this.gpeecList = this.singleValueToList(value.destinataireChargeGpec ?? null);
    this.drhList = this.singleValueToList(value.destinataireDrh ?? null);
    this.dgList = this.singleValueToList(value.destinataireDg ?? null);
    this.responsableList = this.singleValueToList(value.destinataireResponsable ?? null);
    this.dgsList = this.singleValueToList(value.destinataireDgs ?? null);
    this.archivageRhList = this.singleValueToList(value.destinataireArchivageRh ?? null);

    this.showDestinataireDirecteurField = this.directeursList.length > 0;
    this.showDestinataireGpeecField = this.gpeecList.length > 0;
    this.showDestinataireDrhField = this.drhList.length > 0;
    this.showDestinataireDgField = this.dgList.length > 0;
    this.showDestinataireResponsableField = this.responsableList.length > 0;
    this.showDestinataireDgsField = this.dgsList.length > 0;
    this.showDestinataireArchivageRhField = this.archivageRhList.length > 0;

    this.refreshGpeecLabelForDisplay(value);
  }

  // Utility to adapt a single entity to the array structure expected by mat-select.
  private singleValueToList<T>(value: T | null | undefined): T[] {
    return value ? [value] : [];
  }

  // Switch between editable data loading and static display when the parent toggles state.
  private handleDisplayOnlyToggle(): void {
    const displayOnly = this.isDisplayOnly();
    if (displayOnly === this.lastDisplayOnly) {
      return;
    }
    this.lastDisplayOnly = displayOnly;
    if (displayOnly) {
      this.populateReadOnlyOptions(this.form.getRawValue() as Partial<DestinatairesFormValue>);
      this.refreshParentValidation();
    } else {
      this.initializeEditableData(this.form.getRawValue() as Partial<DestinatairesFormValue>, true);
    }
  }

  // Treat readOnly flag, disabled state, or parent disable as the consultation signal.
  private isDisplayOnly(): boolean {
    return this.readOnly || this.isDisabledByParent || this.form.disabled;
  }

  // Re-enables the Responsable select and reapplies its validators.
  private enableDestinataireResponsableField(): void {
    const control = this.form.get('destinataireResponsable');
    if (control) {
      control.setValidators(Validators.required);
      this.setControlInteractivity(control, true);
    }
    this.showDestinataireResponsableField = true;
    this.refreshParentValidation();
  }

  // Clears and disables the Directeur field when no validator is available.
  hideDestinataireDirecteurField(): void {
    const control = this.form.get('destinataireValidation');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireDirecteurField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  // Clears and disables the GPEEC field when the list is empty.
  hideDestinataireGpeecField(): void {
    const control = this.form.get('destinataireChargeGpec');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireGpeecField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  // Clears and disables the DRH field if the backend returns nothing.
  hideDestinataireDrhField(): void {
    const control = this.form.get('destinataireDrh');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireDrhField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  // Clears and disables the DG field when no validator matches.
  hideDestinataireDgField(): void {
    const control = this.form.get('destinataireDg');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireDgField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  // Clears and disables the Responsable field when there is no candidate.
  hideDestinataireResponsableField(): void {
    const control = this.form.get('destinataireResponsable');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireResponsableField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  // Clears and disables the DGS field when no validator exists.
  hideDestinataireDgsField(): void {
    const control = this.form.get('destinataireDgs');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireDgsField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  // Clears and disables the Archivage RH field when unassigned.
  hideDestinataireArchivageRhField(): void {
    const control = this.form.get('destinataireArchivageRh');
    if (control) {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
      this.setControlInteractivity(control, false);
    }
    this.showDestinataireArchivageRhField = false;
    this.emitValueToParent();
    this.refreshParentValidation();
  }

  private emitValueToParent(): void {
    this.onChange(this.form.getRawValue() as DestinatairesFormValue);
  }

  // Ensures the GPEEC label stays consistent in read-only mode (same inputs as edit mode).
  private refreshGpeecLabelForDisplay(value?: Partial<DestinatairesFormValue>): void {
    const fallbackDirection = value?.directionDemande ?? null;
    const directionId = this.extractId(this.direction ?? fallbackDirection);
    const serviceId = this.extractId(this.service ?? null);

    if (!directionId) {
      this.libelleGpeecListe = 'Chargé de gestion GPEEC';
      return;
    }
    const reloadId = ++this.latestGpeecLabelReloadId;
    this.agentsService
      .getChargedGPEEC(this.demandeType, directionId, serviceId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (reloadId !== this.latestGpeecLabelReloadId) {
            return;
          }
          this.libelleGpeecListe = this.getGpeecLabel(data?.type);
        },
        error: (error) => console.error('Erreur lors de la récupération du libellé GPEEC :', error)
      });
  }

  // Formats validator entries for display inside mat-select options.
  validatorDisplayFn(user: User): string {
    return user ? `${user.nom} ${user.prenom}` : '';
  }

  // Stops internal subjects when the component is disposed.
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Compares two user entities by identifier to support select comparison.
  compareId = (user1: User | null, user2: User | null): boolean => {
    const id1 = this.extractId(user1);
    const id2 = this.extractId(user2);
    return !!id1 && !!id2 && id1 === id2;
  };

  // Maps the backend enum to a readable GPEEC field label.
  private getGpeecLabel(typeValideurs: string): string {
    switch (typeValideurs) {
      case 'GESTIONNAIRE_STATUTAIRE':
        return 'Gestionnaire Statutaire';
      case 'CHARGES_GESTION_RH':
        return 'Chargé de gestion RH';
      case 'CHARGES_GESTION_GPEEC':
        return 'Chargé de gestion GPEEC';
      default:
        return 'Non positionné';
    }
  }

  // Lists invalid inner control names to ease parent-side debugging.
  public getInvalidControls(): string[] {
    const invalidControls: string[] = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidControls.push(`DestinatairesForm: ${name}`);
      }
    }
    return invalidControls;
  }

  // Applies enable/disable state to a control while respecting read-only flags.
  private setControlInteractivity(control: AbstractControl | null, shouldEnable: boolean): void {
    if (!control) {
      return;
    }
    const enableControl = shouldEnable && !this.readOnly && !this.isDisabledByParent;
    if (enableControl) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  // Keeps all child controls in sync with the component's read-only configuration.
  private applyInteractivityForAllControls(): void {
    if (!this.form) {
      return;
    }

    this.setControlInteractivity(this.form.get('destinataires'), true);
    this.setControlInteractivity(this.form.get('destinataireValidation'), this.showDestinataireDirecteurField);
    this.setControlInteractivity(this.form.get('destinataireChargeGpec'), this.showDestinataireGpeecField);
    this.setControlInteractivity(this.form.get('destinataireDrh'), this.showDestinataireDrhField);
    this.setControlInteractivity(this.form.get('destinataireDg'), this.showDestinataireDgField);
    this.setControlInteractivity(this.form.get('destinataireResponsable'), this.showDestinataireResponsableField);
    this.setControlInteractivity(this.form.get('destinataireDgs'), this.showDestinataireDgsField);
    this.setControlInteractivity(this.form.get('destinataireArchivageRh'), this.showDestinataireArchivageRhField);

    this.refreshParentValidation();
  }
}
